// this file should mirror gatsby-ssr.tsx
import {
  PrismicPreviewProvider,
  RepositoryConfig,
} from 'gatsby-plugin-prismic-previews';
import * as React from 'react';
import {hydrateRoot} from 'react-dom/client';
import {I18nextProvider} from 'react-i18next';

import i18n from '@src/i18n';
import {getLanguageCodeFromPathname} from '@src/i18n/utils';

import {resolvePrismicLink} from './lib/prismic';

import type {GatsbyBrowser} from 'gatsby';

const languages = require('./lib/gatsby/data/languages.json');

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
  if (process.env.NODE_ENV === 'development') {
    // Gatsby's fast-refresh runtime-error overlay (the purple modal) mounts
    // into this custom element (see gatsby/cache-dir/shadow-portal.js). We
    // knowingly tolerate the CallRail-related React 18 hydration errors — see
    // onRecoverableError below — so hide the dev-only overlay they trigger. It
    // never ships to prod: the overlay is stripped from production builds.
    const style = document.createElement('style');
    // Hiding the overlay host with `display: none` does NOT unmount the React
    // tree inside its shadow DOM, so the overlay's `Overlay` effect — which
    // calls lock-body's `lock()` on mount and `unlock()` only on unmount —
    // leaves `document.body { overflow: hidden }` (and a padding-right jump)
    // stuck forever, silently killing page scroll. Counteract the lock here:
    // an `!important` stylesheet rule overrides the inline style the locker
    // sets. Dev-only, so it can't affect real body-scroll-lock behavior in prod.
    style.textContent = [
      'gatsby-fast-refresh { display: none !important; }',
      'body { overflow: auto !important; padding-right: 0 !important; }',
    ].join('\n');
    document.head.appendChild(style);
  }
};

export const onInitialClientRender: GatsbyBrowser['onInitialClientRender'] =
  () => {
    const docElement = document.documentElement;

    // Change `no-js` to `js` (via Modernizr)
    const reJS = new RegExp('(^|\\s)no-js(\\s|$)');
    docElement.className = docElement.className.replace(reJS, '$1js$2');
  };

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = ({action}) => {
  // Ensure scroll resets when clicking back button from anchor link
  if (action === 'POP') {
    window.scrollTo(0, 0);
  }
};

// @ts-ignore
export const replaceHydrateFunction: GatsbyBrowser['replaceHydrateFunction'] =
  () => {
    return (element: React.ReactNode, container: Document | Element) => {
      const root = hydrateRoot(container, element, {
        onRecoverableError: (error, errorInfo) => {
          if (
            // these first two conditions are really to appease TypeScript
            // we should be able to assume both of these things
            !(error instanceof Error) ||
            !window.DD_RUM ||
            // hydration errors became endemic after upgrading to React 18
            // they appear to all be related to CallRail (see the callrail-dni plugin)
            // we don't control this JS, they don't offer another approach, and we'd rather have the upgrade
            /Minified React error #(418|422|425);/.test(error.message)
          ) {
            return;
          }

          // Try to log to Datadog, fall back to console in dev
          if (window.DD_RUM && typeof window.DD_RUM.addError === 'function') {
            window.DD_RUM.addError(error);
          } else if (process.env.NODE_ENV === 'development') {
            console.warn(
              'DD_RUM.addError not available, error would have been:',
              error,
            );
          }
        },
      });

      return () => root.unmount();
    };
  };

// gatsby-source-prismic docs has two recommendations for performance:
// - defining this array in a constant outside the wrapRootElement() function
// - using React.lazy() to ensure that bundles doe not include the code for each template on every page
const repositoryConfigs: RepositoryConfig[] = [
  {
    componentResolver: {
      article: React.lazy(() => import('./src/templates/Article')),
      marketing_landing_page: React.lazy(
        () => import('./src/templates/MarketingLandingPage'),
      ),
      page: React.lazy(() => import('./src/templates/BasicPage')),
    },
    linkResolver: doc => resolvePrismicLink(doc, languages),
    // gatsby-config.ts gets this value from process.env
    // hardcode it here because it won't be set in the built artifacts that end users download
    repositoryName: 'devoted-health-2020',
  },
];

export const wrapRootElement: GatsbyBrowser['wrapRootElement'] = ({
  element,
}) => {
  // Get the current pathname so we can match server-side behavior in gatsby-ssr.tsx
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : '/';

  return (
    <I18nextProvider
      i18n={i18n.cloneInstance({
        lng: getLanguageCodeFromPathname(pathname, languages),
      })}>
      <React.Suspense
        fallback={
          <div>
            <h1>Loading preview…</h1>
          </div>
        }>
        <PrismicPreviewProvider repositoryConfigs={repositoryConfigs}>
          {element}
        </PrismicPreviewProvider>
      </React.Suspense>
    </I18nextProvider>
  );
};
